/**
 * Espace Formateur, Espace Entreprise, Espace Stagiaire endpoints
 */

import { ENTITE_REFERENCE } from '../../utils/alertis-societes.js'

export const formateurPortailEndpoints = {
  login: () => '/api/portail/auth/login',
  logout: () => '/api/portail/auth/logout',
  me: () => '/api/portail/auth/me',
  resetPassword: () => '/api/portail/auth/reset-password',
  // Passerelle : le compte existe déjà dans l'autre réseau, il ne reste qu'à
  // accepter les conditions de celui-ci pour le rejoindre.
  rejoindreReseau: () => '/api/portail/auth/rejoindre',
  // Ouverture de session par le laissez-passer émis au bout du formulaire
  // d'inscription : le mot de passe y a déjà été prouvé.
  connexionParPasserelle: () => '/api/portail/auth/passerelle',
  // Coordonnées d'une fiche de l'annuaire Global Prévention (connexion requise).
  annuaireCoordonnees: (slug) => `/api/portail/annuaire/${encodeURIComponent(slug)}/coordonnees`,
  missions: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/portail/missions${qs}`
  },
  missionsSollicitations: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/portail/missions/sollicitations${qs}`
  },
  missionsOpportunites: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/portail/missions/opportunites${qs}`
  },
  missionsSearch: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/portail/missions/search${qs}`
  },
  categories: () => '/api/portail/categories',
  publierMission: () => '/api/portail/missions/publier',
  mesPublications: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/portail/missions/mes-publications${qs}`
  },
  supprimerMaPublication: (id) => `/api/portail/missions/${id}`,
  missionPositionnements: (id) => `/api/portail/missions/${id}/positionnements`,
  validerPrestataire: (id, formateurId) => `/api/portail/missions/${id}/valider/${formateurId}`,
  partagerDocumentsMission: (id) => `/api/portail/missions/${id}/partager-documents`,
  missionDocuments: (id) => `/api/portail/missions/${id}/documents`,
  formateursDisponibles: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/portail/missions/formateurs-disponibles${qs}`
  },
  repondre: (id) => `/api/portail/missions/${id}/repondre`,
  // Auto-formations (quiz)
  autoFormationsCatalogue: () => '/api/portail/auto-formations/catalogue',
  autoFormationTheme: (slug) => `/api/portail/auto-formations/themes/${encodeURIComponent(slug)}`,
  autoFormationModule: (id) => `/api/portail/auto-formations/modules/${id}`,
  autoFormationQuiz: (id) => `/api/portail/auto-formations/quiz/${id}`,
  autoFormationQuizReponses: (id) => `/api/portail/auto-formations/quiz/${id}/reponses`,
  autoFormationAttestations: () => '/api/portail/auto-formations/attestations',
  autoFormationAttestation: (id) => `/api/portail/auto-formations/attestations/${id}`,
  mesFormations: () => '/api/portail/mes-formations',
  mesFormationDetail: (id) => `/api/portail/mes-formations/${id}`,
  ajouterStagiaire: (id) => `/api/portail/mes-formations/${id}/stagiaires`,
  confirmerEmargement: (id) => `/api/portail/mes-formations/${id}/emargement/confirm`,
  overrideEmargement: (id) => `/api/portail/mes-formations/${id}/emargement/manual`,
  genererEmargements: (id) => `/api/portail/mes-formations/${id}/emargement/generate`,
  feuilleEmargement: (id) => `/api/portail/mes-formations/${id}/feuille-emargement`,
  contrat: (id) => `/api/portail/mes-formations/${id}/contrat`,
  signerContrat: (id) => `/api/portail/mes-formations/${id}/contrat/signer`,
  contrats: () => '/api/portail/contrats',
  contratCadreDownload: (entite = ENTITE_REFERENCE) =>
    `/api/portail/contrat-cadre/download?entite=${entite}`,
  contratCadreSigner: () => '/api/portail/contrat-cadre/signer',
  // La charte du formateur, jumelle du contrat cadre : même garde d'accès,
  // même réponse `{ downloadUrl, fileName }`. Elle ne se signe pas depuis le
  // portail (l'accord est donné à l'étape 7 de l'inscription), d'où la seule
  // entrée de téléchargement.
  charteDownload: (entite = ENTITE_REFERENCE) =>
    `/api/portail/charte/download?entite=${entite}`,
  charteSigner: () => '/api/portail/charte/signer',
  conventionDownload: (id) => `/api/portail/mes-formations/${id}/convention/download`,
  conventionSigner: (id) => `/api/portail/mes-formations/${id}/convention/signer`,
  profil: () => '/api/portail/profil',
  messagesUnreadCount: () => '/api/portail/messages/unread-count',
  messagesPresence: () => '/api/portail/messages/presence',
  messagesHeartbeat: () => '/api/portail/messages/heartbeat',
  messages: () => '/api/portail/messages',
  documents: () => '/api/portail/documents',
  documentDownload: (docId) => `/api/portail/documents/${docId}/download`,
  documentRaw: (docId) => `/api/portail/documents/${docId}/raw`,
  documentDelete: (docId) => `/api/portail/documents/${docId}`,
  documentUpdate: (docId) => `/api/portail/documents/${docId}`,
  documentShare: () => '/api/portail/documents/share',
  documentEnvois: (docId) => `/api/portail/documents/${docId}/envois`,
  // Comptes rendus (génération PDF, archivés comme documents privés)
  compteRenduEvacuation: () => '/api/portail/comptes-rendus/evacuation',
  // GET : données du formulaire + historique des modifications ; PUT : modification (PDF régénéré)
  compteRendu: (docId) => `/api/portail/comptes-rendus/${docId}`,
  // Matériel : registre d'entretien des mannequins
  mannequins: () => '/api/portail/materiel/mannequins',
  mannequin: (id) => `/api/portail/materiel/mannequins/${id}`,
  mannequinInterventions: (id) => `/api/portail/materiel/mannequins/${id}/interventions`,
  mannequinIntervention: (id, interventionId) => `/api/portail/materiel/mannequins/${id}/interventions/${interventionId}`,
  mannequinRegistre: (id, disposition) => `/api/portail/materiel/mannequins/${id}/registre${disposition ? `?disposition=${disposition}` : ''}`,
  // Streaming same-origin du registre pour l'iframe (la CSP frame-src interdit S3)
  mannequinRegistreRaw: (id) => `/api/portail/materiel/mannequins/${id}/registre/raw`,
  // CV par thème de formation
  cvThemes: () => '/api/portail/cv-themes',
  cvTheme: (categorie) => `/api/portail/cv-themes/${encodeURIComponent(categorie)}`,
  cvThemeGenerate: (categorie) => `/api/portail/cv-themes/${encodeURIComponent(categorie)}/generate`,
  // Calendrier iCal
  calendar: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/portail/calendar${qs}`
  },
  updateIcalUrl: () => '/api/portail/ical-url',
  deleteIcalUrl: () => '/api/portail/ical-url',
  // Système d'avis (annuaire Global Prévention)
  avisRecherche: (q) => '/api/portail/avis/recherche?q=' + encodeURIComponent(q || ''),
  avisMesAvis: () => '/api/portail/avis/mes-avis',
  avisRecus: () => '/api/portail/avis/recus',
  // La note PUBLIQUE et son détail : la même source que le profil de l'annuaire,
  // pour que la page cesse d'afficher une moyenne calculée chez elle.
  avisRecusStats: () => '/api/portail/avis/recus/stats',
  avisCreate: () => '/api/portail/avis',
  avisUpdate: (id) => `/api/portail/avis/${id}`,
  avisDelete: (id) => `/api/portail/avis/${id}`,
  avisReponse: (id) => `/api/portail/avis/${id}/reponse`,
  avisSignalement: (id) => `/api/portail/avis/${id}/signalement`,
  reponseSignalement: (id) => `/api/portail/avis/reponse/${id}/signalement`,
  // Contenus sponsorisés — pool (session formateur) + tracking public (href / beacon)
  advertisements: (emplacement) => '/api/portail/advertisements?emplacement=' + encodeURIComponent(emplacement),
  advertisementClick: (id, emplacement) => '/api/advertisements/' + id + '/click?emplacement=' + encodeURIComponent(emplacement),
  advertisementImpression: (id) => '/api/advertisements/' + id + '/impression',
}

export const entrepriseEndpoints = {
  login: () => '/api/entreprise/auth/login',
  logout: () => '/api/entreprise/auth/logout',
  me: () => '/api/entreprise/auth/me',
  resetPassword: () => '/api/entreprise/auth/reset-password',
  dashboard: () => '/api/entreprise/dashboard',
  stagiaires: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/entreprise/stagiaires${qs}`
  },
  stagiaireDetail: (id) => `/api/entreprise/stagiaires/${id}`,
  attestationDownload: (id) => `/api/entreprise/stagiaires/${id}/attestation-download`,
  documents: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/entreprise/documents${qs}`
  },
  documentDownload: (id) => `/api/entreprise/documents/${id}/download`,
  documentsZip: () => '/api/entreprise/documents/download-zip',
}

export const stagiaireEndpoints = {
  login: () => '/api/stagiaire/auth/login',
  logout: () => '/api/stagiaire/auth/logout',
  me: () => '/api/stagiaire/auth/me',
  dashboard: () => '/api/stagiaire/dashboard',
  formations: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/stagiaire/formations${qs}`
  },
  formationDetail: (inscriptionId) => `/api/stagiaire/formations/${inscriptionId}`,
  documentDownload: (id) => `/api/stagiaire/documents/${id}/download`,
  attestationDownload: () => '/api/stagiaire/attestation-recyclage/download',
  questionnaires: () => '/api/stagiaire/questionnaires',
  conventionSignature: (inscriptionId) => `/api/stagiaire/formations/${inscriptionId}/convention-signature`,
}

// Vérification publique des attestations d'auto-formation (QR code, sans auth)
export const publicAutoFormationsEndpoints = {
  verifier: (ref) => `/api/public/auto-formations/verifier/${encodeURIComponent(ref)}`,
}
